import kebab from "lodash.kebabcase";
import toastMessages from "~/data/content/toast-messages.json";

import type { Profile } from "~/types/profile";

type ProfileContentType =
   // | "classes" // handled in useClassesStore
   | "teachables" // Lesson Plans
   | "coreWorks"
   | "units" // Units & Sequences
   | "pd"
   | "media";

export function useProfile(
   initialState: Profile.State = {
      teachables: null,
      coreWorks: null,
      units: null,
      pd: null,
      media: null,
      meta: {},
      filter: {
         options: [],
         active: null,
      },
   },
) {
   const $apiFetch = useNuxtApp().$apiFetch as typeof $fetch;
   const { updateUserData } = useAuthStore();
   const { log, logInfo, logError } = useDebugger();
   const { addToast } = useToast();

   // >> STATE ========================
   const profile = useState<Profile.State>("profile", () => initialState);

   // >> QUERIES ========================
   const getProfileMeta = async (): Promise<Profile.MetaData> => {
      try {
         const res = await $apiFetch<Profile.MetaData>("users/profile");
         profile.value.meta = res;
         updateUserData(res, true);
         return res;
      } catch (err) {
         logError("getProfileMeta failed", err);
         throw err;
      }
   };

   const getProfilePd = async (): Promise<Profile.PdData> => {
      try {
         const data = await $apiFetch<Profile.PdData>("users/pd");
         logInfo("getProfilePd", data);
         profile.value.pd = data;
         return data;
      } catch (err) {
         logError("getProfilePd failed", err);
         throw err;
      }
   };

   // Self-contained widget UX: shows its own success/error toasts and does not re-throw.
   const uploadAvatar = async (formData: FormData): Promise<void> => {
      try {
         const res = await $apiFetch<Profile.UploadAvatarResponse>(
            "users/profile/avatar",
            { body: formData, method: "POST" },
         );
         if (profile.value.meta && "image" in profile.value.meta) {
            profile.value.meta.image = res.image;
         }
         updateUserData({ image: res.image });
         addToast(toastMessages.uploadAvatar.success as Toast);
      } catch (err: unknown) {
         logError("uploadAvatar failed", err);
         const status =
            (err as { statusCode?: number; status?: number })?.statusCode ??
            (err as { statusCode?: number; status?: number })?.status;
         if (status === 413) {
            addToast(toastMessages.uploadAvatar.serverError as Toast);
         } else {
            addToast(toastMessages.uploadAvatar.error as Toast);
         }
      }
   };

   const removeAvatar = async (): Promise<void> => {
      try {
         await $apiFetch<ApiSuccess>("users/profile/avatar", {
            method: "DELETE",
         });
         updateUserData({ image: null });
      } catch (err) {
         logError("removeAvatar failed", err);
         throw err;
      }
   };

   const getExportData = async (): Promise<Profile.ExportDataResponse> => {
      try {
         return await $apiFetch<Profile.ExportDataResponse>("users/export-data");
      } catch (err) {
         logError("getExportData failed", err);
         throw err;
      }
   };

   const getProfileContent = async (
      type: ProfileContentType,
   ): Promise<unknown> => {
      let apiType = kebab(type);
      if (apiType === "units") apiType = "planning-tools";
      try {
         // Each profile sub-list returns a different shape; consumers narrow
         // via the per-feature namespaces. State is widened to unknown.
         const data = await $apiFetch<unknown>(`users/${apiType}`);
         logInfo("getProfileContent", data);
         profile.value[type] = data as never;
         return data;
      } catch (err) {
         logError("getProfileContent failed", err);
         throw err;
      }
   };

   // Form opt-out: returns { error, status } on 422 so the caller can show
   // inline field-level validation messages via form.setErrors(res.error.errors).
   const updatePassword = async (
      formData: FormData | Record<string, unknown>,
   ): Promise<ApiSuccess | FormErrorReturn> => {
      try {
         return await $apiFetch<ApiSuccess>("users/profile/password-reset", {
            body: formData,
            method: "PATCH",
         });
      } catch (err: unknown) {
         logError("updatePassword failed", err);
         return extractFormError(err);
      }
   };

   // Form opt-out: see updatePassword comment.
   const updateProfile = async (
      formData: FormData | Record<string, unknown>,
   ): Promise<Profile.UpdateProfileResponse | FormErrorReturn> => {
      try {
         const data = await $apiFetch<Profile.UpdateProfileResponse>(
            "users/profile",
            { body: formData, method: "PATCH" },
         );
         log("updateProfile", data);
         updateUserData(data.user);
         return data;
      } catch (err: unknown) {
         logError("updateProfile failed", err);
         return extractFormError(err);
      }
   };

   // >> MEDIA ========================
   const attachMedia = async (
      id: number,
      type: string,
      METHOD: "DELETE" | "POST" = "POST",
   ): Promise<unknown> => {
      try {
         const res = await $apiFetch<unknown>(`media/${id}/save`, {
            method: METHOD,
            body: { type },
         });
         // Refresh profile media list — best-effort; a failed refresh
         // shouldn't make a successful save look like a failure to the caller.
         try {
            await getProfileContent("media");
         } catch (refreshErr) {
            logError("attachMedia profile refresh failed", refreshErr);
         }
         return res;
      } catch (err) {
         logError("attachMedia failed", err);
         throw err;
      }
   };
   // >> HELPERS ========================
   const clearProfile = () => {
      profile.value.teachables = null;
      profile.value.coreWorks = null;
      profile.value.units = null;
      profile.value.pd = null;
      profile.value.media = null;
   };

   // SETTERS ========================
   const setProfilePd = (data: Profile.PdData): void => {
      profile.value.pd = data;
   };
   const setProfileTeachables = (data: unknown[]) => {
      profile.value.teachables = data;
   };
   // GETTERS ========================
   const profileMeta = computed(() => profile.value.meta);
   const profileFilter = computed(() => profile.value.filter);
   const profilePd = computed(() => profile.value.pd);

   // RETURN ========================
   return {
      profile,
      getProfileMeta,
      profileMeta,
      getProfileContent,
      clearProfile,
      profileFilter,
      getExportData,
      updatePassword,
      updateProfile,
      removeAvatar,
      uploadAvatar,
      getProfilePd,
      setProfilePd,
      setProfileTeachables,
      profilePd,
      attachMedia,
   };
}
